/**
 * Scan-payload hygiene, shared verbatim by every scan entry point (POS scan
 * box, price check, returns picker, web Search-by-EAN, mobile stock take /
 * GIN / box packing, and the backend resolvers). Pure TypeScript, no
 * dependencies — keep every copy identical.
 *
 * Why: the tills and handhelds use 2D imagers, which decode whichever symbol
 * they see first. FMCG packs increasingly print a marketing QR
 * (https://qr.brand.in/...) right beside the EAN, so a product scan can arrive
 * as a URL. That string must never reach product lookup — an exact match
 * finds nothing and the partial / name LIKE searches run on garbage — but it
 * must not simply be rejected either, because some QR codes DO identify a
 * product:
 *
 *   - GS1 Digital Link (ISO/IEC 18975): https://id.gs1.org/01/08901088129831
 *     carries the GTIN in the path (`/01/<gtin>`). GS1 is moving packs to
 *     this format (Sunrise 2027), so the GTIN is extracted and searched as
 *     the EAN.
 *   - GS1 element strings from a DataMatrix / QR — `(01)08901088129831(17)..`
 *     or the bare `0108901088129831...` a wedge types — AI 01 is the GTIN.
 *   - Store-printed QR labels carry a plain EAN / SKU. They pass through
 *     unchanged, as does every 1D barcode and every typed search term.
 *
 * Anything else that is recognisably not a product code (any URL, a vCard,
 * a UPI / tel / mailto payload) is classified `ignored` so the caller can
 * clear the field and tell the operator, instead of showing "not found".
 */

export type ScanResolution =
  /** Ordinary barcode / SKU / typed text — look it up as-is. */
  | { kind: 'code'; code: string }
  /**
   * A GTIN was extracted from a GS1 payload. `code` is the form most likely
   * stored (EAN-13 for a GTIN-14 with one leading zero); `candidates` lists
   * every equivalent form (EAN-13, UPC-A, EAN-8, GTIN-14) for resolvers that
   * can match several at once.
   */
  | {
      kind: 'gtin';
      code: string;
      candidates: string[];
      gtin: string;
      source: 'digital-link' | 'gs1-element';
    }
  /** Not a product code. `raw` is what was scanned, for the message. */
  | { kind: 'ignored'; reason: 'url' | 'non-product' | 'empty'; raw: string };

export type IgnoredScan = Extract<ScanResolution, { kind: 'ignored' }>;

/** AIM symbology identifier a scanner can be set to prefix: "]E0", "]Q1", "]d2". */
const AIM_ID_RE = /^\][A-Za-z][0-9]/;
/** FNC1 / GS separator (0x1D) some wedges type for GS1 symbols. */
const GS_RE = /\u001d/g;

/** "https://", "HTTP://", "upi://", "intent://" — any scheme with authority. */
const SCHEME_URL_RE = /^[a-z][a-z0-9+.-]*:\/\//i;
/** "www.brand.in/..." without a scheme. */
const WWW_RE = /^www\./i;
/**
 * "qr.saffola.in/connect?qr=1" — a dotted host with an alphabetic TLD and a
 * path / query. A bare host with no path is left alone: nothing in a product
 * code looks like one, and an exact lookup of it is harmless.
 */
const BARE_HOST_PATH_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}[/?#]/i;
/** Non-web payloads that QR codes commonly carry. */
const NON_PRODUCT_SCHEME_RE =
  /^(mailto|tel|sms|smsto|mms|geo|wifi|market|intent|upi|bitcoin|matmsg|mecard):/i;
const STRUCTURED_RE = /^BEGIN:(VCARD|VCALENDAR|VEVENT)\b/i;

/**
 * GS1 Digital Link: `/01/<gtin>` (or the `/gtin/` long form). The GTIN may be
 * 8, 12, 13 or 14 digits and is followed by another path segment, a query,
 * a fragment, or the end of the URI.
 */
const DIGITAL_LINK_GTIN_RE = /\/(?:01|gtin)\/(\d{8}|\d{12,14})(?=[/?#]|$)/i;
/** Human-readable element string: "(01)08901088129831(17)261231(10)AB12". */
const ELEMENT_BRACKET_RE = /^\(01\)(\d{14})/;
/**
 * Bare element string as a wedge types it: "01" + GTIN-14, then either the
 * end, another AI (digits), a GS char, or a bracketed AI. Needs 16+ digits
 * up front, so a plain EAN-13 / EAN-8 / SKU can never match.
 */
const ELEMENT_BARE_RE = /^01(\d{14})(?=$|\d|\u001d|\()/;

/** Trim and strip scanner framing (AIM prefix, GS chars) — never content. */
function stripFraming(raw: string): string {
  let s = (raw ?? '').trim();
  if (AIM_ID_RE.test(s)) s = s.slice(3);
  return s.replace(GS_RE, '').trim();
}

/**
 * Every stored form a GTIN may have. Our 1D scanners type the digits printed
 * on the pack — EAN-13 for Indian FMCG, UPC-A for imports, EAN-8 for small
 * packs — so the DB holds that form, not the zero-padded GTIN-14. Order is
 * most-likely first; the GTIN-14 and the raw digits close the list.
 */
export function gtinCandidates(gtin: string): string[] {
  const digits = (gtin ?? '').replace(/\D/g, '');
  if (!digits) return [];
  const g14 = digits.padStart(14, '0');
  const out: string[] = [];
  const push = (c: string) => {
    if (c && !out.includes(c)) out.push(c);
  };
  // A shorter form is valid only when the dropped leading digits are zeros.
  for (const len of [13, 12, 8]) {
    if (/^0+$/.test(g14.slice(0, 14 - len))) push(g14.slice(14 - len));
  }
  push(g14);
  push(digits);
  return out;
}

/** True when the string is a URL / web address (any case). */
export function looksLikeUrl(value: string): boolean {
  const s = (value ?? '').trim();
  if (!s) return false;
  return SCHEME_URL_RE.test(s) || WWW_RE.test(s) || BARE_HOST_PATH_RE.test(s);
}

/** Classify one scan (or typed entry). Never throws. */
export function resolveScanCode(raw: string): ScanResolution {
  const s = stripFraming(raw);
  if (!s) return { kind: 'ignored', reason: 'empty', raw: raw ?? '' };

  // GS1 element strings (DataMatrix / QR carrying AIs) — GTIN is AI 01.
  const bracket = ELEMENT_BRACKET_RE.exec(s);
  if (bracket) return gtinResolution(bracket[1], 'gs1-element');
  const bare = ELEMENT_BARE_RE.exec(s);
  if (bare) return gtinResolution(bare[1], 'gs1-element');

  if (looksLikeUrl(s)) {
    // A Digital Link identifies the product — pull the GTIN out of the path.
    const dl = DIGITAL_LINK_GTIN_RE.exec(s);
    if (dl) return gtinResolution(dl[1], 'digital-link');
    return { kind: 'ignored', reason: 'url', raw: s };
  }

  if (NON_PRODUCT_SCHEME_RE.test(s) || STRUCTURED_RE.test(s)) {
    return { kind: 'ignored', reason: 'non-product', raw: s };
  }

  return { kind: 'code', code: s };
}

function gtinResolution(
  gtin: string,
  source: 'digital-link' | 'gs1-element',
): ScanResolution {
  const candidates = gtinCandidates(gtin);
  return { kind: 'gtin', code: candidates[0], candidates, gtin, source };
}

/**
 * Convenience for lookups that take one code: the code to search, or null
 * when the scan must be dropped (URL / non-product / empty).
 */
export function scanCodeForLookup(raw: string): string | null {
  const r = resolveScanCode(raw);
  return r.kind === 'ignored' ? null : r.code;
}

/**
 * Every code a resolver should try for this scan. Plain codes give
 * themselves; a GS1 payload gives all GTIN forms; junk gives [].
 */
export function scanCodeCandidates(raw: string): string[] {
  const r = resolveScanCode(raw);
  if (r.kind === 'gtin') return r.candidates;
  if (r.kind === 'code') return [r.code];
  return [];
}

/** True when a non-empty scan is junk (URL / vCard / UPI …) with no GTIN in it. */
export function isNonProductScan(raw: string): boolean {
  const r = resolveScanCode(raw);
  return r.kind === 'ignored' && r.reason !== 'empty';
}

/**
 * Operator-facing one-liner for an ignored scan. Kept here so the POS, web
 * and mobile wording stays the same.
 */
export function ignoredScanMessage(r: IgnoredScan): string {
  return r.reason === 'url'
    ? "QR code ignored — scan the product's barcode (EAN)"
    : 'Not a product code — scan the barcode (EAN) or enter the SKU';
}
